/**
 * Calendar windows and schedule cards (M4.2). Relative phrases ("this week", "هالأسبوع",
 * "today", "next week") are resolved here, in the person's zone, so the model never guesses
 * a range: the tool layer passes the resolved range back as data. The week starts on Sunday
 * in Gulf zones and on Monday elsewhere. Calendar results are rendered as a card, not prose.
 */

export type WindowName =
  'today' | 'tomorrow' | 'yesterday' | 'this_week' | 'next_week' | 'last_week' | 'next_7_days';

export const WINDOW_NAMES: readonly WindowName[] = [
  'today',
  'tomorrow',
  'yesterday',
  'this_week',
  'next_week',
  'last_week',
  'next_7_days',
];

/** Zones whose working week starts on Sunday. */
export const SUNDAY_START_ZONES: readonly string[] = [
  'Asia/Qatar',
  'Asia/Riyadh',
  'Asia/Dubai',
  'Asia/Kuwait',
  'Asia/Bahrain',
  'Asia/Muscat',
  'Asia/Baghdad',
  'Asia/Amman',
  'Africa/Cairo',
];

/** 0 = Sunday … 6 = Saturday. */
export function weekStartDay(timezone: string): 0 | 1 {
  return SUNDAY_START_ZONES.includes(timezone) ? 0 : 1;
}

export interface LocalDate {
  year: number;
  month: number;
  day: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  hour: number;
  minute: number;
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** Calendar fields of an instant in a zone. */
export function localDate(at: Date, timezone: string): LocalDate {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    hourCycle: 'h23',
  }).formatToParts(at);
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return {
    year: Number(get('year')),
    month: Number(get('month')),
    day: Number(get('day')),
    weekday: Math.max(0, WEEKDAYS.indexOf(get('weekday'))),
    hour: Number(get('hour')) % 24,
    minute: Number(get('minute')),
  };
}

/** The instant of local midnight for a calendar day in a zone (two-pass offset fix). */
export function zonedMidnight(year: number, month: number, day: number, timezone: string): Date {
  let guess = new Date(Date.UTC(year, month - 1, day, 0, 0, 0));
  for (let i = 0; i < 2; i++) {
    const l = localDate(guess, timezone);
    const asUtc = Date.UTC(l.year, l.month - 1, l.day, l.hour, l.minute);
    const want = Date.UTC(year, month - 1, day, 0, 0);
    guess = new Date(guess.getTime() - (asUtc - want));
  }
  return guess;
}

/** Local midnight of the day `at` falls on, plus `days`. */
export function startOfLocalDay(at: Date, timezone: string, days = 0): Date {
  const l = localDate(at, timezone);
  const base = zonedMidnight(l.year, l.month, l.day, timezone);
  if (days === 0) return base;
  // Step by calendar days (a DST-free approach for the Gulf; elsewhere the two-pass fix holds).
  const shifted = new Date(Date.UTC(l.year, l.month - 1, l.day + days, 12));
  const s = localDate(shifted, 'UTC');
  return zonedMidnight(s.year, s.month, s.day, timezone);
}

/** Local midnight that starts the week containing `at`. */
export function startOfLocalWeek(at: Date, timezone: string): Date {
  const l = localDate(at, timezone);
  const first = weekStartDay(timezone);
  const back = (l.weekday - first + 7) % 7;
  return startOfLocalDay(at, timezone, -back);
}

export interface ResolvedWindow {
  window: WindowName;
  /** ISO instants. */
  start: string;
  end: string;
  timezone: string;
  week_start: 0 | 1;
  label: { en: string; ar: string };
  /** True for windows that look back (the person asked in the past tense). */
  past: boolean;
}

// M5.4: range labels in MSA, from this one table.
const LABELS: Record<WindowName, { en: string; ar: string }> = {
  today: { en: 'today', ar: 'اليوم' },
  tomorrow: { en: 'tomorrow', ar: 'غداً' },
  yesterday: { en: 'yesterday', ar: 'أمس' },
  this_week: { en: 'this week', ar: 'هذا الأسبوع' },
  next_week: { en: 'next week', ar: 'الأسبوع القادم' },
  last_week: { en: 'last week', ar: 'الأسبوع الماضي' },
  next_7_days: { en: 'the next 7 days', ar: 'الأيام السبعة القادمة' },
};

export function windowLabel(window: WindowName, lang: Lang): string {
  return LABELS[window][lang];
}

/**
 * Resolve a relative window in the person's zone. "this week" and "today" run from now to
 * the end of the period (what is left); "next week" and "tomorrow" are whole periods; the
 * past windows are whole periods that already ended.
 */
export function resolveWindow(
  window: WindowName,
  ctx: { now: Date; timezone: string },
): ResolvedWindow {
  const { now, timezone } = ctx;
  const week_start = weekStartDay(timezone);
  let start: Date;
  let end: Date;
  let past = false;
  switch (window) {
    case 'today':
      start = now;
      end = startOfLocalDay(now, timezone, 1);
      break;
    case 'tomorrow':
      start = startOfLocalDay(now, timezone, 1);
      end = startOfLocalDay(now, timezone, 2);
      break;
    case 'yesterday':
      start = startOfLocalDay(now, timezone, -1);
      end = startOfLocalDay(now, timezone, 0);
      past = true;
      break;
    case 'this_week': {
      const weekStart = startOfLocalWeek(now, timezone);
      start = now;
      end = startOfLocalDay(weekStart, timezone, 7);
      break;
    }
    case 'next_week': {
      const weekStart = startOfLocalWeek(now, timezone);
      start = startOfLocalDay(weekStart, timezone, 7);
      end = startOfLocalDay(weekStart, timezone, 14);
      break;
    }
    case 'last_week': {
      const weekStart = startOfLocalWeek(now, timezone);
      start = startOfLocalDay(weekStart, timezone, -7);
      end = weekStart;
      past = true;
      break;
    }
    case 'next_7_days':
    default:
      start = now;
      end = new Date(now.getTime() + 7 * 86_400_000);
      break;
  }
  return {
    window,
    start: start.toISOString(),
    end: end.toISOString(),
    timezone,
    week_start,
    label: LABELS[window],
    past,
  };
}

/** The window that follows (for the "Nothing left … next … starts with" line). */
export function followingWindow(window: WindowName): WindowName | undefined {
  if (window === 'today') return 'tomorrow';
  if (window === 'this_week') return 'next_week';
  return undefined;
}

/**
 * Relative phrases in both languages → a window name, when the message clearly uses one. The
 * tool layer uses this as a guard: the model is told the window it should have passed.
 */
export function detectWindow(text: string): WindowName | undefined {
  const t = text.toLowerCase();
  const past = /\b(did|had|was|were)\b|شنو كان|ماذا كان|وش كان|كان عندي/u.test(t);
  if (/\bthis week\b|هالأسبوع|هذا الأسبوع|الأسبوع الحالي|هالاسبوع|هذا الاسبوع/u.test(t))
    return 'this_week';
  if (/\bnext week\b|الأسبوع الجاي|الأسبوع القادم|الأسبوع المقبل|الاسبوع الجاي/u.test(t))
    return 'next_week';
  if (/\blast week\b|الأسبوع الماضي|الاسبوع الماضي|الأسبوع اللي فات/u.test(t)) return 'last_week';
  if (/\btomorrow\b|بكرة|بكره|غداً|غدا/u.test(t)) return 'tomorrow';
  if (/\byesterday\b|أمس|امس|البارحة|البارح/u.test(t)) return 'yesterday';
  if (/\btoday\b|اليوم|النهاردة/u.test(t)) return past ? 'yesterday' : 'today';
  return undefined;
}

// ---------------------------------------------------------------------------
// Schedule card
// ---------------------------------------------------------------------------

export interface ScheduleItem {
  id: string;
  /** ISO instants. */
  start: string;
  end: string;
  /** "09:00–10:00" in the person's zone, Western numerals. */
  time: string;
  title: string;
  all_day?: boolean | undefined;
  attendees?: string[] | undefined;
  /** Account email when more than one account contributed. */
  account?: string | undefined;
  /** Care note for the footnote (another zone, another account). */
  note?: string | undefined;
  /** Slots: the chat message "Use this" sends. */
  use_text?: string | undefined;
}

export interface ScheduleDay {
  /** YYYY-MM-DD in the person's zone. */
  date: string;
  /** Day header in the person's language, e.g. "Thursday 25 Sep" / "الخميس 25 سبتمبر". */
  label: string;
  items: ScheduleItem[];
}

/** The structured artifact the interface renders as a card (protocol frame type "schedule"). */
export interface ScheduleCard {
  card_id: string;
  kind: 'events' | 'slots';
  timezone: string;
  range: { start: string; end: string; window?: WindowName | undefined; label: string };
  with_attendees: boolean;
  days: ScheduleDay[];
  /** One line for anything that needs care; never "approximately". */
  footnote?: string | undefined;
}

export type Lang = 'en' | 'ar';

const LOCALE = { en: 'en-US', ar: 'ar-u-nu-latn' } as const;

/** IANA zone → city, in both languages; the fallback is the zone's last segment. */
export const CITY_NAMES: Record<string, { en: string; ar: string }> = {
  'Asia/Qatar': { en: 'Doha', ar: 'الدوحة' },
  'Asia/Dubai': { en: 'Dubai', ar: 'دبي' },
  'Asia/Riyadh': { en: 'Riyadh', ar: 'الرياض' },
  'Asia/Muscat': { en: 'Muscat', ar: 'مسقط' },
  'Asia/Kuwait': { en: 'Kuwait', ar: 'الكويت' },
  'Asia/Bahrain': { en: 'Manama', ar: 'المنامة' },
  'Asia/Amman': { en: 'Amman', ar: 'عمّان' },
  'Asia/Baghdad': { en: 'Baghdad', ar: 'بغداد' },
  'Asia/Beirut': { en: 'Beirut', ar: 'بيروت' },
  'Africa/Cairo': { en: 'Cairo', ar: 'القاهرة' },
  'Europe/London': { en: 'London', ar: 'لندن' },
  'Europe/Paris': { en: 'Paris', ar: 'باريس' },
  'Europe/Istanbul': { en: 'Istanbul', ar: 'إسطنبول' },
  'America/New_York': { en: 'New York', ar: 'نيويورك' },
};

export function cityOf(timezone: string, lang: Lang = 'en'): string {
  const known = CITY_NAMES[timezone];
  if (known) return known[lang];
  const last = timezone.split('/').pop() ?? timezone;
  return last.replace(/_/g, ' ');
}

export function formatTime(iso: string, timezone: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(iso));
}

export function formatDayLabel(iso: string, timezone: string, lang: Lang): string {
  const parts = new Intl.DateTimeFormat(LOCALE[lang], {
    timeZone: timezone,
    weekday: 'long',
    day: 'numeric',
    month: lang === 'ar' ? 'long' : 'short',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

export function localDayKey(iso: string, timezone: string): string {
  const l = localDate(new Date(iso), timezone);
  return `${l.year}-${String(l.month).padStart(2, '0')}-${String(l.day).padStart(2, '0')}`;
}

export interface CardEventInput {
  id: string;
  title: string;
  start: string;
  end: string;
  timezone?: string | undefined;
  all_day?: boolean | undefined;
  status?: string | undefined;
  attendees?: { email: string; name?: string | undefined }[] | undefined;
  account?: string | null | undefined;
}

/** Group events by local day; cancelled ones are dropped; care notes become one footnote. */
export function buildEventsCard(input: {
  card_id: string;
  events: readonly CardEventInput[];
  range: ResolvedWindow | { start: string; end: string; label: { en: string; ar: string } };
  timezone: string;
  lang: Lang;
  with_attendees: boolean;
  accounts: number;
}): ScheduleCard {
  const { timezone, lang } = input;
  const days = new Map<string, ScheduleDay>();
  const notes: string[] = [];
  const events = [...input.events]
    .filter((e) => e.status !== 'cancelled')
    .sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
  for (const e of events) {
    const key = localDayKey(e.start, timezone);
    const day = days.get(key) ?? {
      date: key,
      label: formatDayLabel(e.start, timezone, lang),
      items: [],
    };
    const time = e.all_day
      ? lang === 'ar'
        ? 'طوال اليوم'
        : 'All day'
      : `${formatTime(e.start, timezone)}–${formatTime(e.end, timezone)}`;
    let note: string | undefined;
    if (e.timezone && e.timezone !== timezone && !e.all_day) {
      const theirs = formatTime(e.start, e.timezone);
      const ours = formatTime(e.start, timezone);
      if (theirs !== ours) {
        note =
          lang === 'ar'
            ? `${e.title}: ${theirs} بتوقيت ${cityOf(e.timezone, 'ar')}، ${ours} في ${cityOf(timezone, 'ar')}`
            : `${e.title}: ${theirs} ${cityOf(e.timezone, 'en')} time, ${ours} in ${cityOf(timezone, 'en')}`;
      }
    }
    if (input.accounts > 1 && e.account) {
      const line = lang === 'ar' ? `${e.title}: من ${e.account}` : `${e.title}: from ${e.account}`;
      note = note ? `${note}; ${line}` : line;
    }
    if (note) notes.push(note);
    day.items.push({
      id: e.id,
      start: e.start,
      end: e.end,
      time,
      title: e.title,
      ...(e.all_day ? { all_day: true } : {}),
      ...(input.with_attendees && e.attendees?.length
        ? { attendees: e.attendees.map((a) => a.name || a.email) }
        : {}),
      ...(input.accounts > 1 && e.account ? { account: e.account } : {}),
      ...(note ? { note } : {}),
    });
    days.set(key, day);
  }
  const range = input.range;
  return {
    card_id: input.card_id,
    kind: 'events',
    timezone,
    range: {
      start: range.start,
      end: range.end,
      ...('window' in range ? { window: range.window } : {}),
      label: range.label[lang],
    },
    with_attendees: input.with_attendees,
    days: [...days.values()],
    ...(notes.length ? { footnote: notes.join(' · ') } : {}),
  };
}

/** Proposed slots as a card; each carries the message "Use this" sends. */
export function buildSlotsCard(input: {
  card_id: string;
  slots: readonly { start: string; end: string }[];
  range: { start: string; end: string; label: { en: string; ar: string } };
  timezone: string;
  lang: Lang;
}): ScheduleCard {
  const { timezone, lang } = input;
  const days = new Map<string, ScheduleDay>();
  for (const [i, s] of input.slots.entries()) {
    const key = localDayKey(s.start, timezone);
    const day = days.get(key) ?? {
      date: key,
      label: formatDayLabel(s.start, timezone, lang),
      items: [],
    };
    const time = `${formatTime(s.start, timezone)}–${formatTime(s.end, timezone)}`;
    const when = `${formatDayLabel(s.start, timezone, lang)}، ${time} (${timezone})`.replace(
      '،',
      lang === 'ar' ? '،' : ',',
    );
    day.items.push({
      id: `slot_${i + 1}`,
      start: s.start,
      end: s.end,
      time,
      title: lang === 'ar' ? 'وقت متاح' : 'Free',
      use_text: lang === 'ar' ? `استخدم الوقت ${when}.` : `Use the slot ${when}.`,
    });
    days.set(key, day);
  }
  return {
    card_id: input.card_id,
    kind: 'slots',
    timezone,
    range: { start: input.range.start, end: input.range.end, label: input.range.label[lang] },
    with_attendees: false,
    days: [...days.values()],
  };
}

/** The one-line reply for an empty window, with the first item of the following window. */
export function emptyWindowLine(
  window: WindowName,
  next: { title: string; start: string } | undefined,
  ctx: { timezone: string; lang: Lang },
): string {
  const { timezone, lang } = ctx;
  const weekday = next
    ? new Intl.DateTimeFormat(LOCALE[lang], { timeZone: timezone, weekday: 'long' }).format(
        new Date(next.start),
      )
    : '';
  const time = next ? formatTime(next.start, timezone) : '';
  if (window === 'this_week') {
    if (!next) {
      return lang === 'ar'
        ? 'لم يبقَ شيء هذا الأسبوع، ولا شيء في الأسبوع القادم بعد.'
        : 'Nothing left this week, and nothing next week yet.';
    }
    return lang === 'ar'
      ? `لم يبقَ شيء هذا الأسبوع. الأسبوع القادم يبدأ بـ${next.title} ${weekday}.`
      : `Nothing left this week. Next week starts with ${next.title} on ${weekday}.`;
  }
  if (window === 'today') {
    if (!next) {
      return lang === 'ar'
        ? 'ما بقي شي اليوم، ولا شي بكرة.'
        : 'Nothing left today, and nothing tomorrow.';
    }
    return lang === 'ar'
      ? `ما بقي شي اليوم. بكرة يبدأ بـ${next.title} الساعة ${time}.`
      : `Nothing left today. Tomorrow starts with ${next.title} at ${time}.`;
  }
  const label = LABELS[window][lang];
  return lang === 'ar' ? `ما فيه شي ${label}.` : `Nothing ${label}.`;
}

/** "Friday 25 September" / «الجمعة 25 سبتمبر»: the long form for a whole day (M5.4). */
export function longDayLabel(iso: string, timezone: string, lang: Lang): string {
  const parts = new Intl.DateTimeFormat(LOCALE[lang], {
    timeZone: timezone,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
  }).formatToParts(new Date(iso));
  const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
  return `${get('weekday')} ${get('day')} ${get('month')}`;
}

/**
 * Whole local days between start and end: start at a local midnight and end at a later local
 * midnight, or at 23:59 of the last day (either way the model may write it). Undefined when
 * the range is not whole days.
 */
export function wholeLocalDays(
  start: string,
  end: string,
  timezone: string,
): { first: string; last: string; days: number } | undefined {
  const a = new Date(start);
  const b = new Date(end);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime()) || b <= a) return undefined;
  if (startOfLocalDay(a, timezone).getTime() !== a.getTime()) return undefined;
  const tail = b.getTime() % 60_000 === 0 ? b : new Date(Math.ceil(b.getTime() / 60_000) * 60_000);
  const endDay = startOfLocalDay(tail, timezone);
  const last =
    endDay.getTime() === tail.getTime()
      ? new Date(endDay.getTime() - 1)
      : tail.getTime() - endDay.getTime() >= 23 * 3_600_000 + 59 * 60_000
        ? tail
        : undefined;
  if (!last) return undefined;
  const lastStart = startOfLocalDay(last, timezone);
  const days = Math.round((lastStart.getTime() - a.getTime()) / 86_400_000) + 1;
  return { first: a.toISOString(), last: lastStart.toISOString(), days };
}

/** The label of an explicit range: a whole day is its date alone, several whole days a span of dates. */
export function explicitRangeLabel(
  start: string,
  end: string,
  timezone: string,
): { en: string; ar: string } | undefined {
  const whole = wholeLocalDays(start, end, timezone);
  if (!whole) return undefined;
  if (whole.days === 1) {
    return {
      en: longDayLabel(whole.first, timezone, 'en'),
      ar: longDayLabel(whole.first, timezone, 'ar'),
    };
  }
  return {
    en: `${longDayLabel(whole.first, timezone, 'en')} – ${longDayLabel(whole.last, timezone, 'en')}`,
    ar: `${longDayLabel(whole.first, timezone, 'ar')} – ${longDayLabel(whole.last, timezone, 'ar')}`,
  };
}
